import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Grid
} from '@mui/material';
import {
  AccessTime,
  Quiz,
  CheckCircle,
  PlayArrow,
  School,
  Refresh,
  RestartAlt
} from '@mui/icons-material';

interface StartTestProps {
  testData: {
    title: string;
    description?: string; // Make this optional to match MockTestData type
    variant?: string;
    timed?: boolean;
    status: string;
    color: string;
    completedQuestions: number;
    totalQuestions: number;
    nextTestIn?: string;
  };
  progressPercentage: number;
  isStarting: boolean;
  onStartTest: () => void;
  // New props for session recovery and progress
  hasExistingProgress?: boolean;
  answeredQuestionCount?: number;
  lastActive?: string;
  // Add totalDuration prop from session
  totalDuration?: number; // in seconds
  // Audio preloading props
  isLoadingAudio?: boolean;
  audioLoadProgress?: { loaded: number; total: number };
}

const StartTest: React.FC<StartTestProps> = ({
  testData,
  progressPercentage,
  isStarting,
  onStartTest,
  hasExistingProgress = false,
  answeredQuestionCount = 0,
  lastActive,
  totalDuration,
  isLoadingAudio = false,
  audioLoadProgress
}) => {
  // Calculate duration in minutes from seconds
  const durationInMinutes = totalDuration ? Math.round(totalDuration / 60) : 90;
  // Determine button text and icon based on test progress
  const getButtonContent = () => {
    if (isLoadingAudio) {
      const progress = audioLoadProgress ? `(${audioLoadProgress.loaded}/${audioLoadProgress.total})` : '';
      return { text: `Loading Audio ${progress}...`, icon: undefined };
    }

    if (isStarting) {
      return { text: 'Starting Test...', icon: undefined };
    }

    if (testData.status !== 'open') {
      if (testData.status === 'upcoming') {
        const opensText = testData.nextTestIn ? `Opens ${testData.nextTestIn}` : 'Opens soon';
        return { text: opensText, icon: <AccessTime /> };
      }
      if (testData.status === 'closed' && testData.timed === false) {
        return { text: 'Start Untimed Test', icon: <PlayArrow /> };
      }
      return { text: 'Test Closed', icon: undefined };
    }

    // Test is open - determine based on progress
    if (testData.completedQuestions === 0) {
      return { text: 'Start Test', icon: <PlayArrow /> };
    } else if (testData.completedQuestions === testData.totalQuestions) {
      return { text: 'Retake Test', icon: <Refresh /> };
    } else {
      return { text: 'Resume Test', icon: <RestartAlt /> };
    }
  };

  const buttonContent = getButtonContent();

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <School sx={{ color: testData.color, mr: 1 }} />
          <Chip 
            label={testData.variant || 'Standard'} 
            color="primary" 
            size="small" 
            sx={{ mr: 2 }}
          />
          <Chip 
            label={testData.status === 'open' ? 'Active' : 'Upcoming'} 
            color={testData.status === 'open' ? 'success' : 'warning'} 
            size="small"
          />
        </Box>
        
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {testData.title}
        </Typography>
        
        {testData.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {testData.description}
          </Typography>
        )}

        {/* Progress Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {testData.completedQuestions} / {testData.totalQuestions}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progressPercentage} 
            sx={{ height: 10, borderRadius: 5 }} 
          />
          
          {/* Session Recovery Information */}
          {hasExistingProgress && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <RestartAlt sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Resume Your Previous Session
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You've answered <strong>{answeredQuestionCount}</strong> question{answeredQuestionCount !== 1 ? 's' : ''} so far.
              </Typography>
              {lastActive && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Last active: {lastActive}
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {progressPercentage.toFixed(1)}% Complete
          </Typography>
        </Box>

        {/* Test Stats */}
        <Grid container spacing={3} sx={{ mb: 4, flexGrow: 1 }}>
          <Grid size={{ xs: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Quiz sx={{ fontSize: 32, color: testData.color, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {testData.totalQuestions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Questions
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <AccessTime sx={{ fontSize: 32, color: testData.color, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {durationInMinutes} min
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Duration
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 32, color: testData.color, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {testData.completedQuestions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Completed
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Dynamic Action Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={onStartTest}
          disabled={
          isStarting ||
          isLoadingAudio ||
          testData.status === 'upcoming' ||
          (testData.status === 'closed' && testData.timed !== false)
        }
          startIcon={buttonContent.icon}
          sx={{
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 600,
            mt: 'auto'
          }}
        >
          {buttonContent.text}
        </Button>
      </CardContent>
    </Card>
  );
};

export default StartTest;